import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useCreateRisk, useRisksList } from "@/application/use-cases/risks/use-risks.queries";
import { useCurrentPhase } from "@/application/use-cases/phases/use-phases.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { WeekSelect } from "@/presentation/components/weeks/week-select";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import type { Rag, Severity, Status } from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/risks/new")({
  component: NewRiskPage,
});

function nextRiskCode(existingCodes: string[]): string {
  const numbers = existingCodes
    .map((c) => /^R(\d+)$/i.exec(c.trim()))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => parseInt(m[1] ?? "0", 10));
  const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  return `R${String(next).padStart(2, "0")}`;
}

function NewRiskPage() {
  const navigate = useNavigate();
  const create = useCreateRisk();
  const currentPhase = useCurrentPhase();
  const stakeholdersQ = useStakeholdersList();
  const risksQ = useRisksList();

  const suggestedCode = nextRiskCode((risksQ.data ?? []).map((r) => r.code));

  const [code, setCode] = useState(suggestedCode);
  const [codeTouched, setCodeTouched] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mitigation, setMitigation] = useState("");
  const [rag, setRag] = useState<Rag>("amber");
  const [severity, setSeverity] = useState<Severity>("medium");
  const [status, setStatus] = useState<Status>("open");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [phaseId, setPhaseId] = useState<string | null>(null);
  const [phaseTouched, setPhaseTouched] = useState(false);

  const effectiveCode = codeTouched ? code : suggestedCode;
  const effectivePhaseId = phaseTouched ? phaseId : (currentPhase.data?.id ?? null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await create.mutateAsync({
        code: effectiveCode.trim().toUpperCase(),
        title,
        description: description || null,
        mitigation: mitigation || null,
        rag,
        severity,
        status,
        owner_id: ownerId,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        phase_id: effectivePhaseId,
      });
      toast.success("Risk created");
      void navigate({ to: "/risks", search: { id: created.id } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create");
    }
  }

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col">
      <PageHeader eyebrow="Register" title="New risk" />
      <form onSubmit={onSubmit} className="flex max-w-2xl flex-col gap-4 px-6 py-6 lg:px-10">
        <div className="grid grid-cols-[120px_1fr] gap-4">
          <Field label="Code">
            <Input
              value={effectiveCode}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(e.target.value);
              }}
              required
              className="font-data uppercase tabular-nums"
              aria-label="Risk code"
            />
          </Field>
          <Field label="Title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
        </div>
        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </Field>
        <Field label="Mitigation">
          <Textarea
            value={mitigation}
            onChange={(e) => setMitigation(e.target.value)}
            rows={3}
            placeholder="What's the plan to defuse this risk?"
          />
        </Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="RAG">
            <Select value={rag} onValueChange={(v) => setRag(v as Rag)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="amber">Amber</SelectItem>
                <SelectItem value="red">Red</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Severity">
            <Select value={severity} onValueChange={(v) => setSeverity(v as Severity)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Owner">
            <Select
              value={ownerId ?? "__none__"}
              onValueChange={(v) => setOwnerId(v === "__none__" ? null : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {(stakeholdersQ.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}{" "}
                    <span className="text-muted-foreground text-[10px] uppercase">· {s.org}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Due date">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>
        <Field label="Week">
          <WeekSelect
            value={effectivePhaseId}
            onChange={(next) => {
              setPhaseTouched(true);
              setPhaseId(next);
            }}
            ariaLabel="Risk week"
          />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: "/risks" })}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
        {label}
      </Label>
      {children}
    </div>
  );
}
